// The app shell: the tab bar and the routes every screen hangs on, the
// theme, the language, and the one data path. Sign-in gates the shell
// without a screen of its own: without a token the browser goes straight
// to the AMV login and back (the hub page's way; the server's own sign-in
// page may already hold a token for this tab — the storage keys are
// shared, so both ways in serve).
import { createEffect, createSignal, For, onMount, Show, type JSX } from "solid-js";
import { A, Navigate, Route, Router, type RouteSectionProps } from "@solidjs/router";
import { createSession, type Session, type SessionStatus } from "@/session/client";
import { WsTransport } from "@/session/ws";
import { IndexedDBPersistence } from "@/session/persist";
import { Lockup } from "@/brand";
import { clientOs, clientVer } from "@/config";
import { signIn, token } from "@/auth";
import { lang, setLang, t } from "@/i18n";
import { Connections, Files, Live, Machines, Notes, Settings, Talk } from "@/screens";

// One session per app, not per route: the socket outlives the tab the
// user is on. The token is read fresh per connection attempt, so an
// attempt after a sign-in carries the newest one.
let theSession: Session | null = null;

function appSession(): Session {
  theSession ??= createSession({
    makeTransport: () => {
      const stored = token();
      if (!stored) throw new Error("no token to dial with");
      return new WsTransport({ origin: location.origin, token: stored.access, ver: clientVer(), os: clientOs() });
    },
    persist: new IndexedDBPersistence(),
  });
  return theSession;
}

const STATUS_KEY: Record<SessionStatus, string> = {
  cached: "status.cached",
  connecting: "status.connecting",
  live: "status.live",
  reconnecting: "status.reconnecting",
  signedout: "status.signedout",
  closed: "status.closed",
};

const TABS = [
  { path: "/talk", key: "tab.talk", en: "Talk" },
  { path: "/live", key: "tab.live", en: "Live" },
  { path: "/connections", key: "tab.connections", en: "Connections" },
  { path: "/machines", key: "tab.machines", en: "Machines" },
  { path: "/notes", key: "tab.notes", en: "Notes" },
  { path: "/files", key: "tab.files", en: "Files" },
  { path: "/settings", key: "tab.settings", en: "Settings" },
] as const;

function LanguageSwitch(): JSX.Element {
  return (
    <span class="seg" role="radiogroup" aria-label={t("lang.label", "Language")}>
      <button type="button" role="radio" aria-checked={lang() === "en"} classList={{ picked: lang() === "en" }} onClick={() => setLang("en")}>
        English
      </button>
      <button type="button" role="radio" aria-checked={lang() === "fi"} classList={{ picked: lang() === "fi" }} onClick={() => setLang("fi")}>
        Suomi
      </button>
    </span>
  );
}

function SignIn(): JSX.Element {
  // The hub's way (server/internal/signin): no sign-in screen of our own
  // — the browser goes straight to the AMV login when there is no token,
  // and a mid-session expiry lands here the same way (the gate listens
  // to the session's signedout). Only a start that failed says so, as
  // text; the retry is the browser's own reload.
  const [failed, setFailed] = createSignal(false);
  onMount(() => {
    signIn().catch(() => setFailed(true));
  });
  return (
    <div class="signin">
      <Lockup />
      <p>
        {failed()
          ? t("signin.failed", "Sign-in did not start — check the connection and reload.")
          : t("signin.opening", "Opening sign-in…")}
      </p>
    </div>
  );
}

// The frame: header with the tabs, the live status word, and the
// language; the screens fill the frame's children.
function Shell(props: RouteSectionProps): JSX.Element {
  const session = appSession();
  // The gate follows the token: closed at boot without one, and closed
  // again when the session itself reports the token gone (an expiry the
  // socket's drops ran into) — the sign-in round trip is a full page
  // load, so this is the one in-page way back to the sign-in view.
  const [authed, setAuthed] = createSignal(Boolean(token()));
  createEffect(() => {
    if (session.status() === "signedout") setAuthed(false);
  });
  return (
    <Show when={authed()} fallback={<SignIn />}>
      <header class="shell">
        <Lockup />
        <span class="status">{t(STATUS_KEY[session.status()], session.status())}</span>
        <nav class="tabs" aria-label="Psypen">
          <For each={TABS}>
            {(tab) => (
              <A href={tab.path} end>
                {t(tab.key, tab.en)}
              </A>
            )}
          </For>
        </nav>
        <LanguageSwitch />
      </header>
      <main>{props.children}</main>
    </Show>
  );
}

export function App(): JSX.Element {
  return (
    <Router root={Shell}>
      <Route path="/" component={() => <Navigate href="/talk" />} />
      <Route path="/talk" component={() => <Talk session={appSession()} />} />
      <Route path="/live" component={() => <Live session={appSession()} />} />
      <Route path="/connections" component={Connections} />
      <Route path="/machines" component={Machines} />
      <Route path="/notes" component={Notes} />
      <Route path="/files" component={Files} />
      <Route path="/settings" component={Settings} />
    </Router>
  );
}
